import { type CyberAssessmentSelectDto } from '@@types/api.types';
import { CyberScore } from '@components/pages/technology/components/cyber/cyber-score.component';
import {
  TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
  TECHNOLOGY_TAB_HISTORY_LIST_CLASS,
} from '@components/pages/technology/components/shared/technology-container-query.constants';
import { formatCyberAssessmentTechnologyTypeLabel } from '@components/pages/technology/components/technology-cyber-report.util';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Score } from '@components/shared/score.component';
import { SurveyCTA } from '@components/shared/survey-cta.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import type { CyberAssessmentDraftProgress } from '@utils/cyber-assessment/model.util';
import { formatDate } from '@utils/date.util';
import { type FC, type ReactNode, useState } from 'react';

type CyberAssessmentHistoryListProps = {
  canCreate: boolean;
  createDisabled: boolean;
  createDisabledReason?: string;
  draftAssessment: CyberAssessmentSelectDto | null;
  draftProgress: CyberAssessmentDraftProgress | null;
  historyAssessments: CyberAssessmentSelectDto[];
  isLoading?: boolean;
  onCreateAssessment: () => void;
  onDiscardDraft: (assessmentId: string) => Promise<void>;
  onResumeDraft: (assessmentId: string) => void;
  onSelectAssessment: (assessmentId: string) => void;
  selectedAssessmentId: string | null;
};

const getCyberAssessmentPercentage = (assessment: CyberAssessmentSelectDto): number => {
  return Math.round((assessment.reportSnapshot?.overview?.cpiRatio ?? 0) * 100);
};

const getCpiScoreClassName = (cpiPercentage: number): string | undefined => {
  return cpiPercentage === 100 ? 'text-xl' : undefined;
};

const getProgressPercentage = (draftProgress: CyberAssessmentDraftProgress | null): number => {
  if (!draftProgress) {
    return 0;
  }

  return draftProgress.progress;
};

const renderCpiTooltipContent = (cpiPercentage: number): ReactNode => {
  return (
    <div className="flex max-w-xs flex-col gap-1">
      <div className="font-semibold text-teal-600">CPI Score: {cpiPercentage}%</div>
      <div className="text-sm">
        The Cyber Posture Index (CPI) measures overall cybersecurity readiness as a ratio of favorable responses to
        total applicable questions.
      </div>
    </div>
  );
};

export const CyberAssessmentHistoryList: FC<CyberAssessmentHistoryListProps> = (props) => {
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const {
    canCreate,
    createDisabled,
    createDisabledReason,
    draftAssessment,
    draftProgress,
    historyAssessments,
    isLoading,
    onCreateAssessment,
    onDiscardDraft,
    onResumeDraft,
    onSelectAssessment,
    selectedAssessmentId,
  } = props;
  const ctaSubtitleText = createDisabled
    ? 'Complete your Military TRL before creating a Cyber Assessment.'
    : historyAssessments.length > 0
      ? 'Revise your Cyber Assessment'
      : 'Get your first Cyber Assessment';

  return (
    <>
      {canCreate && (
        <SurveyCTA
          ctaButtonText="New Cyber Assessment"
          ctaSubtitleText={ctaSubtitleText}
          disabled={createDisabled}
          disabledReason={createDisabledReason}
          draftCard={
            draftAssessment
              ? {
                  isSelected: false,
                  onDiscardClick: () => {
                    setIsDiscardModalOpen(true);
                  },
                  onResumeClick: () => onResumeDraft(draftAssessment.id),
                  progress: getProgressPercentage(draftProgress),
                  renderSummary: () => (
                    <Score
                      score={null}
                      size="medium"
                      subtitle={
                        <span className="text-xs font-light whitespace-nowrap text-[#1F1C1AB2]">Assessment</span>
                      }
                      title={
                        <div className="flex items-center gap-2">
                          <BlueLabel text="Cyber" />
                          <Badge
                            size="xs"
                            variant="primary"
                          >
                            Draft
                          </Badge>
                        </div>
                      }
                    />
                  ),
                  resumeButtonText: 'Resume',
                }
              : undefined
          }
          hasExistingScore={historyAssessments.length > 0}
          hideSubtitleWhenDisabled
          onStartClick={onCreateAssessment}
        />
      )}

      {draftAssessment && (
        <ConfirmDeleteModal
          isOpen={isDiscardModalOpen}
          itemName="cyber assessment"
          onConfirm={async () => onDiscardDraft(draftAssessment.id)}
          setIsOpen={setIsDiscardModalOpen}
        />
      )}

      {historyAssessments.length > 0 && (
        <Tile isLoading={isLoading}>
          <div className="flex flex-col gap-2">
            <p className="my-1 text-base font-medium text-gray-700">Cyber Assessment History</p>

            <div className={TECHNOLOGY_TAB_HISTORY_LIST_CLASS}>
              {historyAssessments.map((assessment) => (
                <CyberAssessmentHistoryItem
                  assessment={assessment}
                  isSelected={selectedAssessmentId === assessment.id}
                  key={assessment.id}
                  onSelect={() => onSelectAssessment(assessment.id)}
                />
              ))}
            </div>
          </div>
        </Tile>
      )}
    </>
  );
};

type CyberAssessmentHistoryItemProps = {
  assessment: CyberAssessmentSelectDto;
  isSelected: boolean;
  onSelect: () => void;
};

const CyberAssessmentHistoryItem: FC<CyberAssessmentHistoryItemProps> = (props) => {
  const { assessment, isSelected, onSelect } = props;
  const cpiPercentage = getCyberAssessmentPercentage(assessment);

  return (
    <button
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md p-2 text-left transition-all duration-200',
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        isSelected
          ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
          : 'bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]',
      )}
      onClick={onSelect}
      type="button"
    >
      <CyberScore
        cpiPercentage={cpiPercentage}
        label="CPI"
        scoreClassName={getCpiScoreClassName(cpiPercentage)}
        scoreContainerClassName="bg-teal-500/12"
        scoreValueContainerClassName="w-8"
        size="md"
        subtitle={
          <div className="mt-0.5 flex flex-col gap-0.5 font-light leading-tight text-gray-600">
            <span className="text-[11px] leading-tight text-gray-600">
              {assessment.technologyTypes.map(formatCyberAssessmentTechnologyTypeLabel).join(' • ')}
            </span>
            <span className="text-xs leading-tight text-gray-500">{formatDate(assessment.updatedAt)}</span>
          </div>
        }
        tooltipContent={renderCpiTooltipContent(cpiPercentage)}
      />
    </button>
  );
};
